import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { ReactNode } from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

// Generate slug from text (same logic as TableOfContents)
function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\u4e00-\u9fff\s-]/g, '') // Keep Chinese characters
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

// Extract text content from React children
function extractText(children: ReactNode): string {
  if (typeof children === 'string') return children;
  if (Array.isArray(children)) return children.map(extractText).join('');
  if (children && typeof children === 'object' && 'props' in children) {
    return extractText(children.props.children);
  }
  return '';
}

export function MarkdownRenderer({ content, className = '' }: MarkdownRendererProps) {
  return (
    <div className={`markdown-body ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          h1: ({ children }) => {
            const text = extractText(children);
            const id = generateSlug(text);
            return <h1 id={id}>{children}</h1>;
          },
          h2: ({ children }) => {
            const text = extractText(children);
            const id = generateSlug(text);
            return <h2 id={id}>{children}</h2>;
          },
          h3: ({ children }) => {
            const text = extractText(children);
            const id = generateSlug(text);
            return <h3 id={id}>{children}</h3>;
          },
          h4: ({ children }) => {
            const text = extractText(children);
            const id = generateSlug(text);
            return <h4 id={id}>{children}</h4>;
          },
          h5: ({ children }) => {
            const text = extractText(children);
            const id = generateSlug(text);
            return <h5 id={id}>{children}</h5>;
          },
          h6: ({ children }) => {
            const text = extractText(children);
            const id = generateSlug(text);
            return <h6 id={id}>{children}</h6>;
          },
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
            >
              {children}
            </a>
          ),
          img: ({ src, alt }) => (
            <img
              src={src}
              alt={alt || ''}
              loading="lazy"
              className="rounded-lg max-w-full h-auto"
            />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
